/**
 * What Home hands a surface (D354): the day strip, the stats row and the feed, as plain
 * versioned data. The desktop draws them itself from the same models (`shared/home/`); a phone
 * is handed `{ v: 1, strip, stats, feed }` unchanged by the host. Text is capped so a surface
 * can lay it out; people are named, never ids.
 */
import { z } from "zod";
export const HOME_VERSION = 1;
const tone = z.enum(["teal", "violet", "amber", "blue", "rose", "green", "slate"]);
export const homeFeedKindSchema = z.enum(["needs-you", "plan", "finished", "decision", "watching", "memory", "routine", "update", "away", "team-run", "team-task", "team-change", "org-push", "team-node", "working", "work", "pr", "dispatch", "app", "mention"]);
/** The phone's views a card can open (additive, 0.89.18): a card sets `open.phone` only where its desktop view has a phone equivalent. */
export const HOME_PHONE_VIEWS = ["inbox", "workspace", "timeline", "plans", "roadmap", "tasks", "memory", "git", "routines", "risks", "apps"];
export const homePhoneViewSchema = z.enum(HOME_PHONE_VIEWS);
export const homePhoneOpenSchema = z.object({ view: homePhoneViewSchema, id: z.string().max(300).optional() });
const open = z.object({ view: z.string().max(80), label: z.string().max(120), phone: homePhoneOpenSchema.optional() });
/** One card of the feed. */
export const homeFeedCardSchema = z.object({
    v: z.literal(HOME_VERSION),
    id: z.string().max(300),
    kind: homeFeedKindSchema,
    tone,
    /** 0 needs you, 1 is blocked on you, 2 is running, 3 finished, 4 changed. */
    tier: z.number().int().min(0).max(4),
    kicker: z.string().max(120),
    title: z.string().max(300),
    summary: z.string().max(400).optional(),
    status: z.string().max(120),
    who: z.string().max(200).optional(),
    where: z.string().max(200).optional(),
    because: z.string().max(200),
    at: z.number(),
    on: z.number().optional(),
    needsYou: z.boolean(),
    /** The card asks something (additive, 0.89.19): it is answered, never put away by Clear or Clear all. */
    asks: z.literal(true).optional(),
    /**
     * Additive (0.89.19): what a compact list groups the card under — the plan it is about (`key` `plan:<planId>`, `label` the
     * plan's name) or else its project (`key` the project's id, `label` its name). A board's columns are `tier`.
     */
    group: z.object({ key: z.string().max(300), label: z.string().max(200) }).optional(),
    /** Additive (0.89.19): the card's one-line summary (its summary's first sentence, or where it stands), for a compact row. */
    line: z.string().max(140).optional(),
    changeKey: z.string().max(300),
    open,
    opens: z.array(open).max(6).optional(),
});
const dot = z.object({
    kind: z.enum(["routine", "plan", "release", "review"]),
    count: z.number().int().nonnegative(),
    names: z.array(z.string().max(200)).max(4),
    owners: z.array(z.string().max(120)).max(8),
});
/** One day of the strip. */
export const homeDaySchema = z.object({
    v: z.literal(HOME_VERSION),
    start: z.number(),
    weekday: z.string().max(3),
    date: z.number().int().min(1).max(31),
    isToday: z.boolean(),
    dots: z.array(dot).max(4),
    summary: z.string().max(300),
    label: z.string().max(600),
});
/** A stat's number as a number, beside its words (additive, 0.89.18): the plan-usage stat's, so a phone can draw a meter. */
export const homeStatMeasureSchema = z.object({ value: z.number(), max: z.number(), percent: z.number().min(0).max(100) });
/** How a stat moved against the period before it (additive, 0.89.18), where there is a previous value. */
export const homeStatTrendSchema = z.object({ delta: z.number(), direction: z.enum(["up", "down", "flat"]), words: z.string().max(120) });
/** One number of the stats row. `value` absent is a dash. */
export const homeStatSchema = z.object({
    v: z.literal(HOME_VERSION),
    id: z.enum(["needs-you", "running", "done", "plan-usage", "cost", "team"]),
    label: z.string().max(60),
    value: z.string().max(40).optional(),
    detail: z.string().max(160),
    tone: z.enum(["amber", "teal", "green", "violet", "blue", "slate"]),
    needsYou: z.boolean().optional(),
    open: z.string().max(80),
    measure: homeStatMeasureSchema.optional(),
    trend: homeStatTrendSchema.optional(),
});
/** Everything Home shows, for a surface that cannot compute it. */
export const homeBundleSchema = z.object({
    v: z.literal(HOME_VERSION),
    strip: z.array(homeDaySchema).max(7),
    stats: z.array(homeStatSchema).max(6),
    feed: z.array(homeFeedCardSchema).max(50),
});
//# sourceMappingURL=home.js.map